import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, extractError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { formatMoney, formatDate } from "../../lib/money";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Plus, Receipt as ReceiptIcon, MessageSquare, Filter, X, Truck, Container, User as UserIcon, AlertTriangle } from "lucide-react";
import ExpenseRowIcons from "../../components/ExpenseRowIcons";

const ANY = "__any__";
const STATUSES = ["pending", "matched", "unknown_resolved"];

function TmsIcon({ type, className = "h-3.5 w-3.5" }) {
  if (type === "truck") return <Truck className={className} />;
  if (type === "trailer") return <Container className={className} />;
  if (type === "driver") return <UserIcon className={className} />;
  return null;
}

function TmsRowCell({ e }) {
  const groups = [
    { type: "truck", items: e.trucks || [] },
    { type: "trailer", items: e.trailers || [] },
    { type: "driver", items: e.drivers || [] },
  ].filter((g) => g.items.length > 0);
  if (groups.length === 0) return <>—</>;
  return (
    <div className="space-y-0.5">
      {groups.map((g) => (
        <div key={g.type} className="inline-flex max-w-full items-center gap-1 text-xs">
          <TmsIcon type={g.type} />
          <span className="truncate max-w-[140px]">{g.items[0].label || g.items[0].id}</span>
          {g.items.length > 1 && (
            <span className="rounded-full bg-slate-100 px-1.5 py-0 text-[10px] font-medium text-slate-600">
              +{g.items.length - 1}
            </span>
          )}
        </div>
      )).reduce((acc, el, i) => i === 0 ? [el] : [...acc, <br key={`br-${i}`} />, el], [])}
    </div>
  );
}

function TmsCardSummary({ e }) {
  const counts = { truck: (e.trucks || []).length, trailer: (e.trailers || []).length, driver: (e.drivers || []).length };
  if (counts.truck + counts.trailer + counts.driver === 0) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500" data-testid={`expense-card-tms-${e.id}`}>
      {counts.truck > 0 && (<><Truck className="h-3 w-3" />×{counts.truck}</>)}
      {counts.trailer > 0 && (<><Container className="h-3 w-3" />×{counts.trailer}</>)}
      {counts.driver > 0 && (<><UserIcon className="h-3 w-3" />×{counts.driver}</>)}
    </span>
  );
}

function TmsFilterTypeahead({ kind, value, onChange }) {
  // value: {id, label} | null  ;  onChange: ({id,label} | null) => void
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!open || q.length < 1) { setResults([]); return; }
    const handle = setTimeout(() => {
      setLoading(true);
      api.get(`/tms/${kind}/search`, { params: { q, limit: 10 } })
        .then((r) => { if (!cancelled) setResults(r.data || []); })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [q, open, kind]);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-[#0F1E3D]/20 bg-[#0F1E3D]/5 px-2 py-1.5 text-sm"
        data-testid={`filter-${kind}-selected`}>
        <span className="truncate text-[#0F1E3D]">{value.label}</span>
        <button type="button" onClick={() => onChange(null)}
          data-testid={`filter-${kind}-clear`}
          className="text-slate-400 hover:text-rose-600">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Input
        value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder={`Search ${kind.slice(0, -1)}…`}
        autoComplete="off"
        data-testid={`filter-${kind}-input`}
      />
      {open && (loading || results.length > 0) && (
        <div className="absolute z-30 mt-1 max-h-44 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-md"
          data-testid={`filter-${kind}-results`}>
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>}
          {!loading && results.map((r) => (
            <button key={r.id} type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange({ id: r.id, label: r.label }); setQ(""); setOpen(false); }}
              data-testid={`filter-${kind}-result-${r.id}`}
              className="block w-full border-b border-slate-100 px-3 py-2 text-left text-sm last:border-0 hover:bg-slate-50">
              {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const cls =
    status === "pending"
      ? "bg-amber-50 text-amber-700"
      : status === "matched"
      ? "bg-emerald-50 text-emerald-700"
      : "bg-slate-100 text-slate-600";
  return <Badge variant="secondary" className={cls}>{status}</Badge>;
}

export default function ExpenseList() {
  const { user } = useAuth();
  const isPrivileged = user?.role === "admin" || user?.role === "accounting";
  const [params, setParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(25);
  const [page, setPage] = useState(parseInt(params.get("page") || "1", 10));

  // Lookups
  const [users, setUsers] = useState([]);
  const [cards, setCards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);

  // Filters
  const [q, setQ] = useState(params.get("q") || "");
  const [userId, setUserId] = useState(params.get("user_id") || ANY);
  const [cardId, setCardId] = useState(params.get("card_id") || ANY);
  const [categoryId, setCategoryId] = useState(params.get("category_id") || ANY);
  const [departmentId, setDepartmentId] = useState(params.get("department_id") || ANY);
  const [companyId, setCompanyId] = useState(params.get("company_id") || ANY);
  const [truckFilter, setTruckFilter] = useState(() => {
    const id = params.get("truck_id");
    return id ? { id, label: id } : null;
  });
  const [trailerFilter, setTrailerFilter] = useState(() => {
    const id = params.get("trailer_id");
    return id ? { id, label: id } : null;
  });
  const [driverFilter, setDriverFilter] = useState(() => {
    const id = params.get("driver_id");
    return id ? { id, label: id } : null;
  });
  const [chargeDriver, setChargeDriver] = useState(params.get("charge_driver") || ANY);
  const [status_, setStatus_] = useState(params.get("status") || ANY);
  const [dateFrom, setDateFrom] = useState(params.get("date_from") || "");
  const [dateTo, setDateTo] = useState(params.get("date_to") || "");
  const [showFilters, setShowFilters] = useState(false);

  const loadLookups = useCallback(async () => {
    const lookups = [
      { key: "cards", url: "/cards/available", set: setCards },
      { key: "categories", url: "/categories/active", set: setCategories },
      { key: "departments", url: "/departments/active", set: setDepartments },
      { key: "companies", url: "/companies/active", set: setCompanies },
    ];
    if (isPrivileged) {
      lookups.push({ key: "users", url: "/users/workers", set: setUsers });
    }
    const results = await Promise.allSettled(lookups.map((l) => api.get(l.url)));
    results.forEach((res, i) => {
      const { key, set } = lookups[i];
      if (res.status === "fulfilled") {
        set(res.value.data);
      } else {
        console.warn(`lookup ${key} failed`, res.reason);
      }
    });
  }, [isPrivileged]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const queryParams = { page, page_size: pageSize };
      if (q) queryParams.q = q;
      if (userId !== ANY) queryParams.user_id = userId;
      if (cardId !== ANY) queryParams.card_id = cardId;
      if (categoryId !== ANY) queryParams.category_id = categoryId;
      if (departmentId !== ANY) queryParams.department_id = departmentId;
      if (companyId !== ANY) queryParams.company_id = companyId;
      if (truckFilter) queryParams.truck_id = truckFilter.id;
      if (trailerFilter) queryParams.trailer_id = trailerFilter.id;
      if (driverFilter) queryParams.driver_id = driverFilter.id;
      if (chargeDriver !== ANY) queryParams.charge_driver = chargeDriver === "true";
      if (status_ !== ANY) queryParams.status = status_;
      if (dateFrom) queryParams.date_from = new Date(`${dateFrom}T00:00:00.000Z`).toISOString();
      if (dateTo) queryParams.date_to = new Date(`${dateTo}T23:59:59.999Z`).toISOString();
      const { data } = await api.get("/expenses", { params: queryParams });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, q, userId, cardId, categoryId, departmentId, companyId, truckFilter, trailerFilter, driverFilter, chargeDriver, status_, dateFrom, dateTo]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { load(); }, [load]);

  // Sync URL
  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (userId !== ANY) next.set("user_id", userId);
    if (cardId !== ANY) next.set("card_id", cardId);
    if (categoryId !== ANY) next.set("category_id", categoryId);
    if (departmentId !== ANY) next.set("department_id", departmentId);
    if (companyId !== ANY) next.set("company_id", companyId);
    if (truckFilter) next.set("truck_id", truckFilter.id);
    if (trailerFilter) next.set("trailer_id", trailerFilter.id);
    if (driverFilter) next.set("driver_id", driverFilter.id);
    if (chargeDriver !== ANY) next.set("charge_driver", chargeDriver);
    if (status_ !== ANY) next.set("status", status_);
    if (dateFrom) next.set("date_from", dateFrom);
    if (dateTo) next.set("date_to", dateTo);
    if (page > 1) next.set("page", String(page));
    setParams(next, { replace: true });
  }, [q, userId, cardId, categoryId, departmentId, companyId, truckFilter, trailerFilter, driverFilter, chargeDriver, status_, dateFrom, dateTo, page, setParams]);

  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const cardsById = useMemo(() => Object.fromEntries(cards.map((c) => [c.id, c])), [cards]);
  const catsById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const projsById = useMemo(() => Object.fromEntries(departments.map((p) => [p.id, p])), [departments]);
  const companiesById = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c])), [companies]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const resetFilters = () => {
    setQ(""); setUserId(ANY); setCardId(ANY); setCategoryId(ANY); setDepartmentId(ANY); setCompanyId(ANY);
    setTruckFilter(null); setTrailerFilter(null); setDriverFilter(null);
    setChargeDriver(ANY);
    setStatus_(ANY); setDateFrom(""); setDateTo(""); setPage(1);
  };

  return (
    <div data-testid="expense-list-page">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            {user?.role === "worker" ? "My expenses" : "All expenses"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {total.toLocaleString()} total · page {page} of {totalPages}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters((v) => !v)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="mr-1 h-4 w-4" /> Filters
          </Button>
          {user?.role === "worker" && (
            <Link to="/expenses/new" data-testid="new-expense-link">
              <Button className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
                <Plus className="mr-1 h-4 w-4" /> New
              </Button>
            </Link>
          )}
        </div>
      </div>

      {showFilters && (
        <Card className="mb-6 border-slate-200" data-testid="filters-card">
          <CardContent className="grid grid-cols-1 gap-3 pt-6 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label className="text-xs text-slate-600">Search vendor</Label>
              <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="vendor…" data-testid="filter-q" />
            </div>
            {isPrivileged && (
              <div>
                <Label className="text-xs text-slate-600">Worker</Label>
                <Select value={userId} onValueChange={(v) => { setUserId(v); setPage(1); }}>
                  <SelectTrigger data-testid="filter-user"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>Any</SelectItem>
                    {users.filter((u) => u.role === "worker").map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-600">Card</Label>
              <Select value={cardId} onValueChange={(v) => { setCardId(v); setPage(1); }}>
                <SelectTrigger data-testid="filter-card"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any</SelectItem>
                  {cards.map((c) => (<SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Category</Label>
              <Select value={categoryId} onValueChange={(v) => { setCategoryId(v); setPage(1); }}>
                <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any</SelectItem>
                  {categories.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Department</Label>
              <Select value={departmentId} onValueChange={(v) => { setDepartmentId(v); setPage(1); }}>
                <SelectTrigger data-testid="filter-department"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any</SelectItem>
                  {departments.map((p) => (<SelectItem key={p.id} value={p.id}>{p.code}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Company</Label>
              <Select value={companyId} onValueChange={(v) => { setCompanyId(v); setPage(1); }}>
                <SelectTrigger data-testid="filter-company"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`filter-company-opt-${c.code}`}>{c.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Truck</Label>
              <TmsFilterTypeahead kind="trucks" value={truckFilter}
                onChange={(v) => { setTruckFilter(v); setPage(1); }} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Trailer</Label>
              <TmsFilterTypeahead kind="trailers" value={trailerFilter}
                onChange={(v) => { setTrailerFilter(v); setPage(1); }} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Driver</Label>
              <TmsFilterTypeahead kind="drivers" value={driverFilter}
                onChange={(v) => { setDriverFilter(v); setPage(1); }} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Charge driver</Label>
              <Select value={chargeDriver} onValueChange={(v) => { setChargeDriver(v); setPage(1); }}>
                <SelectTrigger data-testid="filter-charge-driver"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any</SelectItem>
                  <SelectItem value="true" data-testid="filter-charge-driver-true">Yes</SelectItem>
                  <SelectItem value="false" data-testid="filter-charge-driver-false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Status</Label>
              <Select value={status_} onValueChange={(v) => { setStatus_(v); setPage(1); }}>
                <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any</SelectItem>
                  {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} data-testid="filter-date-from" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} data-testid="filter-date-to" />
            </div>
            <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
              <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="reset-filters-btn">
                <X className="mr-1 h-4 w-4" /> Reset
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert variant="destructive" className="mb-4" data-testid="list-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <p className="py-10 text-center text-slate-500">Loading…</p>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 px-6 py-16 text-center" data-testid="empty-state">
          <ReceiptIcon className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-700">No expenses found</p>
          <p className="text-xs text-slate-500">Try adjusting filters or log a new expense.</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden rounded-md border border-slate-200 bg-white md:block">
            <table className="w-full text-sm" data-testid="expense-table">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Vendor</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Card</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium">Company</th>
                  <th className="px-4 py-3 font-medium">Department</th>
                  <th className="px-4 py-3 font-medium">TMS</th>
                  <th className="px-4 py-3 font-medium">CD</th>
                  {isPrivileged && <th className="px-4 py-3 font-medium">Worker</th>}
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
                    <td className="px-4 py-3 text-slate-700">{formatDate(e.occurred_at)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <Link to={`/expenses/${e.id}`} className="hover:underline">{e.vendor}</Link>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{formatMoney(e.amount_cents, e.currency)}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {e.card_label || cardsById[e.card_id]?.label || "—"}{" "}
                      <span className="font-mono text-xs text-slate-400">•••• {e.card_last4 || cardsById[e.card_id]?.last4 || "????"}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{e.category_name || catsById[e.category_id]?.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600" data-testid={`expense-row-company-${e.id}`}>{e.company_code || (e.company_id ? companiesById[e.company_id]?.code : "") || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{e.department_code || (e.department_id ? projsById[e.department_id]?.code : "") || "—"}</td>
                    <td className="px-4 py-3 text-slate-600" data-testid={`expense-row-tms-${e.id}`}>
                      <TmsRowCell e={e} />
                    </td>
                    <td className="px-4 py-3" data-testid={`expense-row-cd-${e.id}`}>
                      {e.charge_driver ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
                          title="Charged back to driver">
                          <AlertTriangle className="h-3 w-3" /> CD
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    {isPrivileged && (
                      <td className="px-4 py-3 text-slate-600" data-testid={`expense-row-worker-${e.id}`}>
                        {e.user_name || e.user_email || usersById[e.user_id]?.email || "—"}
                      </td>
                    )}
                    <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                    <td className="px-4 py-3 text-right text-slate-400">
                      <span className="flex items-center justify-end gap-1.5">
                        <ExpenseRowIcons expense={e} />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="space-y-3 md:hidden" data-testid="expense-cards">
            {items.map((e) => (
              <li key={e.id} data-testid={`expense-card-${e.id}`}>
                <Link to={`/expenses/${e.id}`}>
                  <Card className="border-slate-200">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-slate-900">{e.vendor}</p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {formatDate(e.occurred_at)} · {e.card_label || cardsById[e.card_id]?.label || "Card"}
                          </p>
                          <div className="mt-1 flex items-center gap-2">
                            <span className="text-xs text-slate-500">{e.category_name || catsById[e.category_id]?.name}</span>
                            {isPrivileged && (e.user_name || e.user_email) && (
                              <span className="text-xs text-slate-400">· {e.user_name || e.user_email}</span>
                            )}
                            {(e.comment || e.receipt_image_id) && <ExpenseRowIcons expense={e} size="sm" />}
                            <TmsCardSummary e={e} />
                            {e.charge_driver && (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700" data-testid={`expense-card-cd-${e.id}`}>
                                <AlertTriangle className="h-2.5 w-2.5" /> CD
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">{formatMoney(e.amount_cents, e.currency)}</p>
                          <div className="mt-1"><StatusBadge status={e.status} /></div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between" data-testid="pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="page-prev">
                Previous
              </Button>
              <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="page-next">
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
